// Cámara y loop de decodificación de barcodes para el modal de escaneo de refunds.
import { useCallback, useEffect, useRef, useState } from 'react'
import { readBarcodes } from 'zxing-wasm/reader'

import {
  BARCODE_READER_OPTIONS,
  applyCameraTrackEnhancements,
  createNativeBarcodeDetector,
  getBarcodeScanRegion,
  getPreferredVideoInputDevices,
  getRefundScanCameraConstraints,
  readNativeBarcodeValues
} from '../helpers/refundScanModalUtils'
import { prepareScanBeep } from '../helpers/refundScanAudio'

const CAMERA_FOCUS_HINT = 'Mantén 15-25 cm de distancia y toca la imagen para enfocar.'
const CAMERA_DISTANCE_HINT =
  'No se pudo leer el código. Aleja un poco el teléfono y toca para enfocar.'
const SCAN_FRAME_INTERVAL_MS = 200
const DECODE_FAILURE_HINT_THRESHOLD = 8

/**
 * @param {{ onDetectedCode: (code: string) => boolean }} options
 * onDetectedCode debe devolver true si el código se procesó (el loop detiene la cámara).
 */
export default function useRefundScanCamera({ onDetectedCode }) {
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraStatus, setCameraStatus] = useState('')
  const [cameraError, setCameraError] = useState('')
  const [cameraCodeError, setCameraCodeError] = useState(false)
  const [cameraDevices, setCameraDevices] = useState([])

  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const scanTimerRef = useRef(null)
  const focusTimerRef = useRef(null)
  const nativeBarcodeDetectorRef = useRef(undefined)
  const activeCameraDeviceIdRef = useRef('')
  const decodingRef = useRef(false)
  const decodeFailureCountRef = useRef(0)
  const onDetectedCodeRef = useRef(onDetectedCode)

  onDetectedCodeRef.current = onDetectedCode

  const getNativeBarcodeDetector = useCallback(async () => {
    if (nativeBarcodeDetectorRef.current !== undefined) {
      return nativeBarcodeDetectorRef.current
    }

    nativeBarcodeDetectorRef.current = await createNativeBarcodeDetector()
    return nativeBarcodeDetectorRef.current
  }, [])

  const refreshCameraDevices = useCallback(async (track) => {
    const activeDeviceId = track?.getSettings?.().deviceId || activeCameraDeviceIdRef.current

    if (!navigator.mediaDevices?.enumerateDevices) {
      if (activeDeviceId) {
        activeCameraDeviceIdRef.current = activeDeviceId
      }

      return {
        activeDeviceId: activeCameraDeviceIdRef.current,
        videoDevices: []
      }
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = getPreferredVideoInputDevices(devices)
      setCameraDevices(videoDevices)

      if (activeDeviceId) {
        activeCameraDeviceIdRef.current = activeDeviceId
      } else if (!activeCameraDeviceIdRef.current && videoDevices[0]?.deviceId) {
        activeCameraDeviceIdRef.current = videoDevices[0].deviceId
      }

      return {
        activeDeviceId: activeCameraDeviceIdRef.current,
        videoDevices
      }
    } catch (_) {
      // Device enumeration can fail on some Android builds; scanning still works.
    }

    if (activeDeviceId) {
      activeCameraDeviceIdRef.current = activeDeviceId
    }

    return {
      activeDeviceId: activeCameraDeviceIdRef.current,
      videoDevices: []
    }
  }, [])

  const stopCamera = useCallback((updateState = true) => {
    if (scanTimerRef.current) {
      window.clearTimeout(scanTimerRef.current)
      scanTimerRef.current = null
    }

    if (focusTimerRef.current) {
      window.clearTimeout(focusTimerRef.current)
      focusTimerRef.current = null
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null
    }

    decodingRef.current = false
    decodeFailureCountRef.current = 0

    if (updateState) {
      setCameraOpen(false)
      setCameraStatus('')
      setCameraError('')
      setCameraCodeError(false)
    }
  }, [])

  // Loop: recorta la región de escaneo y prueba BarcodeDetector nativo + zxing-wasm.
  const scanCameraFrame = useCallback(async () => {
    const video = videoRef.current
    const canvas = canvasRef.current

    if (!streamRef.current || !video || !canvas) return

    if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
      if (!decodingRef.current) {
        decodingRef.current = true

        try {
          const context = canvas.getContext('2d', { willReadFrequently: true })
          if (context) {
            const scanRegion = getBarcodeScanRegion({
              videoWidth: video.videoWidth,
              videoHeight: video.videoHeight,
              displayWidth: video.clientWidth,
              displayHeight: video.clientHeight
            })

            if (!scanRegion) return

            canvas.width = scanRegion.width
            canvas.height = scanRegion.height
            context.drawImage(
              video,
              scanRegion.x,
              scanRegion.y,
              scanRegion.width,
              scanRegion.height,
              0,
              0,
              canvas.width,
              canvas.height
            )

            const nativeDetector = await getNativeBarcodeDetector()
            const nativeBarcodeValues = await readNativeBarcodeValues(nativeDetector, canvas)
            for (const nativeBarcodeValue of nativeBarcodeValues) {
              const matched = onDetectedCodeRef.current?.(nativeBarcodeValue)
              if (matched) {
                stopCamera()
                return
              }
            }

            const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
            const results = await readBarcodes(imageData, BARCODE_READER_OPTIONS)
            const result = results.find((item) => item?.text)

            if (result?.text) {
              const matched = onDetectedCodeRef.current?.(result.text)
              if (matched) {
                stopCamera()
                return
              }
            }

            decodeFailureCountRef.current += 1
            if (decodeFailureCountRef.current >= DECODE_FAILURE_HINT_THRESHOLD) {
              setCameraStatus(CAMERA_DISTANCE_HINT)
              decodeFailureCountRef.current = 0
            }
          }
        } catch (_) {
          decodeFailureCountRef.current += 1
          if (decodeFailureCountRef.current >= DECODE_FAILURE_HINT_THRESHOLD) {
            setCameraStatus(CAMERA_DISTANCE_HINT)
            decodeFailureCountRef.current = 0
          }
        } finally {
          decodingRef.current = false
        }
      }
    }

    scanTimerRef.current = window.setTimeout(scanCameraFrame, SCAN_FRAME_INTERVAL_MS)
  }, [getNativeBarcodeDetector, stopCamera])

  const refocusCamera = useCallback(async () => {
    const track = streamRef.current?.getVideoTracks?.()[0]
    if (!track) return

    setCameraStatus('Reenfocando cámara...')
    setCameraCodeError(false)
    const enhanced = await applyCameraTrackEnhancements(track, { refocus: true })

    window.setTimeout(async () => {
      if (!streamRef.current) return

      // Single-shot focus can remain locked on a blurry frame; resume continuous autofocus.
      await applyCameraTrackEnhancements(track, { focusOnly: true })
      if (!streamRef.current) return

      setCameraStatus(
        enhanced
          ? CAMERA_FOCUS_HINT
          : 'Aleja un poco el teléfono y mantén el código dentro del recuadro.'
      )
    }, 900)
  }, [])

  const openCameraWithDevice = useCallback(
    async (deviceId = '') => {
      prepareScanBeep()
      stopCamera(false)
      setCameraOpen(true)
      setCameraError('')
      setCameraCodeError(false)
      setCameraStatus('Solicitando acceso a la cámara...')
      decodeFailureCountRef.current = 0

      try {
        await new Promise((resolve) => window.setTimeout(resolve, 0))

        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('camera_unavailable')
        }

        const stream = await navigator.mediaDevices.getUserMedia(
          getRefundScanCameraConstraints(deviceId)
        )

        streamRef.current = stream

        if (videoRef.current) {
          videoRef.current.srcObject = stream
          videoRef.current.contentHint = 'detail'
          await videoRef.current.play()
        }

        const videoTrack = stream.getVideoTracks?.()[0]
        const trackDeviceId = videoTrack?.getSettings?.().deviceId || deviceId
        if (trackDeviceId) {
          activeCameraDeviceIdRef.current = trackDeviceId
        }

        const enhancedCamera = await applyCameraTrackEnhancements(videoTrack)
        await refreshCameraDevices(videoTrack)

        setCameraStatus(
          enhancedCamera ? CAMERA_FOCUS_HINT : 'Enfoca el código de barra dentro del recuadro.'
        )
        focusTimerRef.current = window.setTimeout(() => {
          if (streamRef.current) {
            refocusCamera()
          }
        }, 650)
        scanTimerRef.current = window.setTimeout(scanCameraFrame, 300)
      } catch (error) {
        stopCamera(false)
        setCameraOpen(true)
        setCameraStatus('')
        setCameraError('Para escanear, permite el acceso a la cámara.')
        setCameraCodeError(false)
      }
    },
    [refocusCamera, refreshCameraDevices, scanCameraFrame, stopCamera]
  )

  const openCamera = useCallback(
    () => openCameraWithDevice(activeCameraDeviceIdRef.current),
    [openCameraWithDevice]
  )

  const switchCamera = useCallback(() => {
    if (cameraDevices.length < 2) return

    const activeDeviceId = activeCameraDeviceIdRef.current
    const activeIndex = cameraDevices.findIndex((device) => device.deviceId === activeDeviceId)
    const nextDevice =
      cameraDevices[(activeIndex + 1 + cameraDevices.length) % cameraDevices.length]

    if (nextDevice?.deviceId) {
      openCameraWithDevice(nextDevice.deviceId)
    }
  }, [cameraDevices, openCameraWithDevice])

  useEffect(() => () => stopCamera(false), [stopCamera])

  return {
    cameraOpen,
    cameraStatus,
    cameraError,
    cameraCodeError,
    canSwitchCamera: cameraDevices.length > 1,
    videoRef,
    canvasRef,
    openCamera,
    refocusCamera,
    switchCamera,
    stopCamera
  }
}
